import { useState } from 'react'
import { Alert, Card, Col, DatePicker, Row, Space, Statistic, Tag, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import type { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePermissions } from '@/shared/auth/usePermissions'
import {
  CASH_FLOW_SYNTHETIC_KEYS,
  downloadFinanceExport,
  fetchCashFlow,
  type CashFlowLine,
} from '@/features/finance/financeApi'
import { CostCenterFilter } from '@/features/finance/CostCenterFilter'
import { OpenPeriodsAlert } from '@/features/finance/OpenPeriodsAlert'
import { ReportScopeNoteBar } from '@/features/finance/ReportScopeNoteBar'
import { StatementSection, type StatementRow } from '@/features/finance/StatementSection'
import { tenantToday } from '@/shared/lib/dayRange'
import { formatMoneyValue, money } from '@/shared/lib/money'
import { seededName } from '@/shared/lib/seededName'
import { PageHeader } from '@/shared/ui/PageHeader'
import { ServerExportButton } from '@/shared/ui/ServerExportButton'

const { RangePicker } = DatePicker
const ISO_DAY = 'YYYY-MM-DD'

const SYNTHETIC: ReadonlySet<string> = new Set(CASH_FLOW_SYNTHETIC_KEYS)

export function FinanceCashFlowPage() {
  const { t } = useTranslation()
  const { can } = usePermissions()

  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => [
    tenantToday().startOf('month'),
    tenantToday(),
  ])
  const [nodeId, setNodeId] = useState<string | null>(null)

  const from = range[0].format(ISO_DAY)
  const to = range[1].format(ISO_DAY)

  const report = useQuery({
    queryKey: queryKeys.finance.cashFlow(from, to, nodeId),
    queryFn: ({ signal }) => fetchCashFlow({ from, to, nodeId }, signal),
  })

  const data = report.data
  const currency = data?.note.currency ?? ''

  const lineRows = (lines: readonly CashFlowLine[]): StatementRow[] =>
    lines.map((line) =>
      SYNTHETIC.has(line.key)
        ? {
            key: line.key,
            code: null,
            label: t(`finance.cashFlow.synthetic.${line.key}`),
            amountMinor: line.amountMinor,
          }
        : {
            key: line.key,
            code: line.code ?? null,
            label: seededName('accounts', line.code, line.name, t),
            amountMinor: line.amountMinor,
          },
    )

  const operatingRows: StatementRow[] = data
    ? [
        {
          key: 'netProfit',
          code: null,
          label: t('finance.cashFlow.netProfit'),
          amountMinor: data.netProfitMinor,
          emphasis: true,
        },
        ...lineRows(data.operating),
      ]
    : []

  const section = (
    title: string,
    rows: readonly StatementRow[],
    totalLabel: string,
    totalMinor: number | undefined,
  ) => (
    <StatementSection
      title={title}
      rows={rows}
      totalLabel={totalLabel}
      totalMinor={totalMinor}
      currency={currency}
      loading={report.isPending}
      emptyText={t('finance.cashFlow.emptySection')}
    />
  )

  return (
    <>
      <PageHeader
        title={t('finance.cashFlow.title')}
        description={t('finance.cashFlow.intro')}
        extra={
          can('report.export') ? (
            <ServerExportButton
              size="middle"
              title={t('finance.cashFlow.title')}
              disabled={report.isError}
              run={(format, onProgress) =>
                downloadFinanceExport('cash-flow', format, { from, to, nodeId, onProgress })
              }
            />
          ) : null
        }
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size="middle" align="center">
          <Typography.Text type="secondary">{t('finance.cashFlow.rangeLabel')}:</Typography.Text>
          <RangePicker
            value={range}
            onChange={(value) => {
              if (value?.[0] && value[1]) setRange([value[0], value[1]])
            }}
            allowClear={false}
            format="DD.MM.YYYY"
          />
          <CostCenterFilter value={nodeId} onChange={setNodeId} />
        </Space>
        <ReportScopeNoteBar note={data?.note} />
      </Card>

      <OpenPeriodsAlert note={data?.note} />

      {report.isError ? (
        <Alert
          type="error"
          showIcon
          message={t('dataTable.loadFailed')}
          description={translateError(report.error, t)}
        />
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('finance.cashFlow.cashOpening')}
                  value={formatMoneyValue(money(data?.cashOpeningMinor ?? 0, currency))}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('finance.cashFlow.netChange')}
                  valueStyle={(data?.netChangeMinor ?? 0) < 0 ? { color: '#cf1322' } : undefined}
                  value={formatMoneyValue(money(data?.netChangeMinor ?? 0, currency))}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('finance.cashFlow.cashClosing')}
                  value={formatMoneyValue(money(data?.cashClosingMinor ?? 0, currency))}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Space direction="vertical" size={4}>
                  <Typography.Text type="secondary">{t('finance.cashFlow.reconciliation')}</Typography.Text>
                  {data ? (
                    <Tag color={data.reconciled ? 'success' : 'error'}>
                      {data.reconciled
                        ? t('finance.cashFlow.reconciled')
                        : t('finance.cashFlow.notReconciled')}
                    </Tag>
                  ) : null}
                </Space>
              </Card>
            </Col>
          </Row>

          {data && !data.reconciled ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('finance.cashFlow.notReconciledTitle')}
              description={t('finance.cashFlow.notReconciledHint')}
            />
          ) : null}

          {section(
            t('finance.cashFlow.operating'),
            operatingRows,
            t('finance.cashFlow.operatingTotal'),
            data?.operatingTotalMinor,
          )}
          {section(
            t('finance.cashFlow.investing'),
            data ? lineRows(data.investing) : [],
            t('finance.cashFlow.investingTotal'),
            data?.investingTotalMinor,
          )}
          {section(
            t('finance.cashFlow.financing'),
            data ? lineRows(data.financing) : [],
            t('finance.cashFlow.financingTotal'),
            data?.financingTotalMinor,
          )}
        </>
      )}
    </>
  )
}
